import './global-styles.scss'

import {BlockInfo} from 'quickstart/types'
import {IS_AUTOTEST, IS_DEV, IS_TEST, memo1} from 'tizra'

import {abstractBlockInfo} from './blocks/AbstractBlock'
import {attachmentsBlockInfo} from './blocks/AttachmentsBlock'
import {bookshelfBlockInfo} from './blocks/BookshelfBlock'
import {browseBlockInfo} from './blocks/BrowseBlock'
import {cartBlockInfo} from './blocks/CartBlock'
import {detailsBlockInfo} from './blocks/DetailsBlock'
import {footerBlockInfo} from './blocks/FooterBlock'
import {globalBlockInfo} from './blocks/GlobalBlock'
import {headerBlockInfo} from './blocks/HeaderBlock'
import {headlineBlockInfo} from './blocks/HeadlineBlock'
import {imageBlockInfo} from './blocks/ImageBlock'
import {markdownBlockInfo} from './blocks/MarkdownBlock'
import {navBlockInfo} from './blocks/NavBlock'
import {postBlockInfo} from './blocks/PostBlock'
import {redemptionBlockInfo} from './blocks/RedemptionBlock'
import {registerUserBlockInfo} from './blocks/RegisterUserBlock'
import {resetPasswordBlockInfo} from './blocks/ResetPasswordBlock'
import {searchBlockInfo} from './blocks/SearchBlock'
import {searchWithinBlockInfo} from './blocks/SearchWithinBlock'
import {signInBlockInfo} from './blocks/SignInBlock'
import {staticPageBlockInfo} from './blocks/StaticPageBlock'
import {subnavBlockInfo} from './blocks/SubnavBlock'
import {themeBlockInfo} from './blocks/ThemeBlock'
import {tocBlockInfo} from './blocks/TocBlock'

const infos = {
  [abstractBlockInfo.name]: abstractBlockInfo,
  [attachmentsBlockInfo.name]: attachmentsBlockInfo,
  [bookshelfBlockInfo.name]: bookshelfBlockInfo,
  [browseBlockInfo.name]: browseBlockInfo,
  [cartBlockInfo.name]: cartBlockInfo,
  [detailsBlockInfo.name]: detailsBlockInfo,
  [footerBlockInfo.name]: footerBlockInfo,
  [globalBlockInfo.name]: globalBlockInfo,
  [headerBlockInfo.name]: headerBlockInfo,
  [headlineBlockInfo.name]: headlineBlockInfo,
  [imageBlockInfo.name]: imageBlockInfo,
  [markdownBlockInfo.name]: markdownBlockInfo,
  [navBlockInfo.name]: navBlockInfo,
  [postBlockInfo.name]: postBlockInfo,
  [redemptionBlockInfo.name]: redemptionBlockInfo,
  [registerUserBlockInfo.name]: registerUserBlockInfo,
  [resetPasswordBlockInfo.name]: resetPasswordBlockInfo,
  [searchBlockInfo.name]: searchBlockInfo,
  [searchWithinBlockInfo.name]: searchWithinBlockInfo,
  [signInBlockInfo.name]: signInBlockInfo,
  [staticPageBlockInfo.name]: staticPageBlockInfo,
  [subnavBlockInfo.name]: subnavBlockInfo,
  [themeBlockInfo.name]: themeBlockInfo,
  [tocBlockInfo.name]: tocBlockInfo,
} as const

export type BlockName = keyof typeof infos

const paletteStyle = (borderColor: string) => ({
  border: `1px solid ${borderColor}`,
  background: '#e6e7e7',
})

const palettes = {
  // original color #c7b4d8
  general: {
    title: 'General Blocks',
    style: paletteStyle('#9f90ad'),
    infos: [markdownBlockInfo, imageBlockInfo],
  },

  content: {
    title: 'Content Blocks',
    style: paletteStyle('#1f77b4'),
    infos: [
      headerBlockInfo,
      abstractBlockInfo,
      detailsBlockInfo,
      attachmentsBlockInfo,
      tocBlockInfo,
      postBlockInfo,
    ],
  },

  index: {
    title: 'Index Blocks',
    style: paletteStyle('#b1e18b'),
    infos: [browseBlockInfo, headlineBlockInfo],
  },

  site: {
    title: 'Site Blocks',
    style: paletteStyle('#b15928'),
    infos: [
      navBlockInfo,
      subnavBlockInfo,
      footerBlockInfo,
      bookshelfBlockInfo,
      searchBlockInfo,
      signInBlockInfo,
      registerUserBlockInfo,
      resetPasswordBlockInfo,
      redemptionBlockInfo,
      staticPageBlockInfo,
    ],
  },

  forms: {
    title: 'Form Blocks',
    style: paletteStyle('#febf6e'),
    infos: [searchWithinBlockInfo],
  },

  commerce: {
    title: 'Commerce Blocks',
    style: paletteStyle('#e6191e'),
    infos: [cartBlockInfo],
  },

  // Unreleased blocks only appear in palettes on autotest or in Storybook on
  // development. They don't appear in palettes on production, but are still
  // available via getBlock so that any layouts with previously-released blocks
  // continue to work.
  unreleased: {
    title: 'UNRELEASED',
    style: paletteStyle('#e6191e'),
    infos: IS_AUTOTEST || (IS_DEV && !IS_TEST) ? [] : [],
  },
} as const

const infosWithStyle = memo1(
  () =>
    Object.fromEntries(
      Object.entries(infos).map(([name, info]) => {
        const palette = Object.values(palettes).find(({infos}) =>
          (infos as BlockInfo<any, any, any>[]).includes(info),
        )
        const style = palette?.style || {}
        return [name, {...info, style}]
      }),
    ) as {
      [N in BlockName]: (typeof infos)[N] & {style: object}
    },
)

export const getInfo = <N extends BlockName>(name: N) => infosWithStyle()[name]

/**
 * Rename infos to blocks for compat with server.
 */
export const getPalettes = memo1(() =>
  Object.entries(palettes)
    .map(([name, {infos, ...p}]) => ({
      name,
      ...p,
      blocks: infos.map(info => getInfo(info.name)),
    }))
    .filter(({blocks}) => blocks.length),
)
